/**
 * AÑADIR UNA ACTUALIZACIÓN AL FOCO desde su ficha (admin/encargado), 22-09-2026:
 * «cómo está» + gravedad + foto/audio/nota, y queda DENTRO del foco sin pasar por
 * «Reportar» y enlazar después. Quien la añade es quien confirma, así que el parte
 * entra pendiente y se confirma en el mismo envío por la ÚNICA puerta de
 * confirmar (`useConfirmScoutingSession`, decisión D2); «Ya no hay» es una
 * revisión limpia que cierra el foco (D8/D14). Sin GPS: el parte se ancla en el
 * sitio del foco (`focus_anchor`).
 *
 * REINTENTAR ES SEGUIR CON EL MISMO PARTE (auditoría 23-09 ciclo#5): el id del parte y el
 * `clientRequestId` se fijan para ESTE foco hasta que sale bien (`pinnedSession.ts`, como F27).
 * Antes cada intento insertaba otro parte: si fallaba la RPC quedaba uno pendiente huérfano, y
 * si la RPC grababa pero se perdía la respuesta, el reintento metía una SEGUNDA actualización
 * en el foco. Y una excepción al subir el audio salía del gancho sin resultado.
 */
import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useConfirmScoutingSession, type ConfirmSeverity } from "@/hooks/useConfirmScoutingSession";
import { usePestFocusAction } from "@/hooks/usePestFocusAction";
import { fetchBlockFociContext } from "@/hooks/useFocusLinkProposal";
import type { AudioTake } from "@/hooks/useAudioRecorder";
import { invalidateScoutingData } from "@/lib/scoutingDataInvalidation";
import { focusErrorOf } from "@/lib/pests/confirmErrors";
import { insertPinned, pinFor, saveMedia, uploadPinnedMedia, type PendingPin } from "@/lib/pests/pinnedSession";
import type { FollowUpVerdict } from "@/lib/pests/followUp";
import type { FocusDetail } from "@/hooks/useFocusTimeline";

export interface FocusUpdateInput {
  verdict: FollowUpVerdict;
  /** Obligatoria salvo «ya no hay» (D7). */
  severity: ConfirmSeverity | null;
  date: string;
  note: string;
  photos: File[];
  audio: AudioTake | null;
  /** «Ya no hay» con tareas vivas: quien cierra ha contestado si se anulan (E, 23-09-2026). */
  cancelTasks?: boolean;
}

export type FocusUpdateResult = { ok: true; closed: boolean } | { ok: false; messageKey: string };

export function useFocusUpdate(focus: FocusDetail | null) {
  const { company, user } = useAuth();
  const queryClient = useQueryClient();
  const { confirm } = useConfirmScoutingSession();
  const focusAction = usePestFocusAction();
  const [busy, setBusy] = useState(false);
  /* El parte fijado para la actualización en curso de ESTE foco (se suelta al salir bien). */
  const pinRef = useRef<PendingPin | null>(null);
  /* Un doble toque antes de que se pinte «ocupado» recibe la MISMA respuesta. */
  const inFlightRef = useRef<Promise<FocusUpdateResult> | null>(null);

  const run = useCallback(
    async (input: FocusUpdateInput): Promise<FocusUpdateResult> => {
      if (!focus || !company?.id || !user?.id) return { ok: false, messageKey: "pests.scouting.focusLink.errors.generic" };
      setBusy(true);
      try {
        const pin = pinFor(pinRef.current, `${company.id}|${focus.id}`);
        pinRef.current = pin;
        const sessionId = pin.sessionId;
        const done = (closed: boolean): FocusUpdateResult => {
          pinRef.current = null;
          invalidateScoutingData(queryClient);
          return { ok: true, closed };
        };

        // 1 · lo que el foco sabe de su sitio (fila y ancla), para que el parte caiga donde él.
        const { data: f } = await supabase
          .from("pest_foci")
          .select("block_row_id, anchor_x, anchor_y, anchor_t, greenhouse_id, block_id, line_number")
          .eq("id", focus.id)
          .maybeSingle();
        const fila = f as { block_row_id: string | null; anchor_x: number | null; anchor_y: number | null; anchor_t: number | null; greenhouse_id: string; block_id: string; line_number: number | null } | null;

        // 2 · el parte, pendiente, con todo lo que se sabe ya. En un reintento, el mismo parte
        //     (con la nota y la fecha de ahora si aún no se confirmó).
        const notes = [fila?.line_number != null ? `Línea: ${fila.line_number}` : null, input.note.trim() || null].filter(Boolean).join("\n") || null;
        const payload: Record<string, unknown> = {
          company_id: company.id,
          inspector_id: user.id,
          date: input.date,
          greenhouse_id: fila?.greenhouse_id ?? focus.greenhouseId,
          block_id: fila?.block_id ?? focus.blockId,
          block_row_id: fila?.block_row_id ?? null,
          /* `manual_map` y no `structured` (22-09, Plan 3): también es un apunte
             manual desde el mapa; `structured` significa «histórico» en los
             informes y se escondía por defecto. Así lleva su insignia y queda
             fuera de «quién reporta». S30 no asserta `source`. */
          source: "manual_map",
          review_status: "pending",
          notes,
          anchor_x: fila?.anchor_x ?? null,
          anchor_y: fila?.anchor_y ?? null,
          anchor_t: fila?.anchor_t ?? null,
          anchor_source: fila?.anchor_x != null ? "focus_anchor" : null,
        };
        const ins = await insertPinned(sessionId, payload, { notes, date: input.date });
        if (!ins.ok) {
          if (ins.stale) pinRef.current = null;
          return { ok: false, messageKey: "pests.scouting.focusLink.errors.generic" };
        }
        const existing = ins.existing;

        // 3 · fotos y audio, con la ruta de siempre (por parte), cada uno en su try/catch: si una
        //     subida falla —o lanza—, el parte sigue valiendo. Lo ya subido no se repite.
        await saveMedia(sessionId, await uploadPinnedMedia(pin, company.id, input.photos, input.audio));

        // El intento anterior YA llegó a la base y solo se perdió la respuesta: está hecho. No se
        // repite (unirlo otra vez contaría otro rebrote; la revisión limpia, otra decisión).
        if (existing?.focus_id === focus.id && existing.triage_status === "dismissed" && existing.dismissal_reason === "clean_check") return done(true);

        // 4 · «ya no hay» = revisión limpia que cierra el foco; si no, confirmar uniéndolo al foco.
        if (input.verdict === "gone") {
          try {
            await focusAction.mutateAsync({ action: "clean_check", focus_id: focus.id, session_id: sessionId, close_focus: true, notes: input.note.trim() || undefined, cancel_tasks: input.cancelTasks });
          } catch (e) {
            return { ok: false, messageKey: focusErrorOf(e).messageKey };
          }
          return done(true);
        }
        if (!input.severity) return { ok: false, messageKey: "pests.scouting.focusLink.errors.severity" };
        if (existing?.focus_id === focus.id && existing.triage_status === "classified") return done(false);
        let seen: string[] = [focus.id];
        try {
          seen = (await fetchBlockFociContext(company.id, focus.blockId)).foci.map((x) => x.id);
        } catch {
          /* con el propio foco basta para unir */
        }
        const out = await confirm({
          sessionId,
          pestId: focus.pestId,
          pestName: focus.pestName,
          severity: input.severity,
          verdict: input.verdict === "unknown" ? null : input.verdict,
          focusAction: "join",
          focusId: focus.id,
          adoptFocusPest: false,
          seenFocusIds: seen,
          clientRequestId: pin.clientRequestId,
          mode: "prefilled_accepted",
          candidates: [],
          proposalKind: "focus_update",
        });
        if (!out.ok) {
          invalidateScoutingData(queryClient);
          return { ok: false, messageKey: out.error.messageKey };
        }
        return done(false);
      } catch {
        // Nada de lo de arriba debería lanzar; si algo lo hace, el diálogo tiene que poder decirlo.
        return { ok: false, messageKey: "pests.scouting.focusLink.errors.generic" };
      } finally {
        setBusy(false);
      }
    },
    [focus, company?.id, user?.id, confirm, focusAction, queryClient],
  );

  const submit = useCallback(
    (input: FocusUpdateInput): Promise<FocusUpdateResult> => {
      if (inFlightRef.current) return inFlightRef.current;
      const p = run(input).finally(() => {
        inFlightRef.current = null;
      });
      inFlightRef.current = p;
      return p;
    },
    [run],
  );

  return { submit, busy };
}
